import { AxiosResponse } from 'axios';
import { HistoryTrackingResponse } from '@/features/shipment/types';
import { parseError } from '@/features/common/helpers/errorParser';

import {
  HistoryItemType, ShipmentSummaryItem, TrackingDetailsType,
} from '@/features/shipment-history/types';
import { useAxios } from '@/features/common/helpers/axios';
import { GlobalResponseType } from '@/features/common/types';
import { getQueryStringForShipments } from '@/features/shipment-history/helpers';
import { filterShipmentHistoryFromResponse } from '../helpers/shipment-history.data';

const { axiosService } = useAxios();
const { axiosService: axiosServiceV2 } = useAxios(2);

export interface ShipmentsQueryParams {
  Offset?: number,
  Limit?: number,
  carriers?: string[],
  fromDate?: string,
  toDate?: string,
  statusTypes?: string[]
  search?: string,
  today?: boolean | null,
  myaccount?: boolean | null,
  deleted?: boolean | null,
  onlyMe?: boolean | null,
}

export interface AddressMapPayload {
  addr1: string,
  city: string,
  province?: string,
  postalCode: string,
  countryCode: string
  countryName: string
}

export interface AddressMapResponse {
  base64StringMap: string,
}

export interface ExportShipmentHistoryDto {
  myAccount: boolean,
  isDontShowSharedUsers: boolean,
  deleted: boolean,
  dateRange: {
    fromDate: string,
    toDate: string,
  }
}

const defaultShipmentsQueryParams: ShipmentsQueryParams = {
  Offset: 0,
  Limit: 20,
  carriers: [],
  fromDate: '',
  toDate: '',
  statusTypes: [],
  search: '',
  today: null,
  myaccount: null,
  deleted: null,
  onlyMe: null,
};

export const createReturnShipment = async (
  trackingNumber: string,
  force: 0 | 1,
): Promise<HistoryTrackingResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      `/history/${trackingNumber}/create-return?force=${force}`,
    );

    const { allowed, isInTransit, isInProgressDetected } = response.data;

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      allowed,
      isInTransit,
      isInProgressDetected,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const editShipmentHistory = async (
  trackingNumber: string,
  force: 0 | 1,
): Promise<HistoryTrackingResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.put(
      `/history/${trackingNumber}?force=${force}`,
    );

    const { allowed, isInTransit, isInProgressDetected } = response.data;

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      allowed,
      isInTransit,
      isInProgressDetected,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const deleteShipmentHistory = async (
  trackingNumber: string,
): Promise<HistoryTrackingResponse | GlobalResponseType> => {
  try {
    await axiosService.delete(
      `/history/${trackingNumber}`,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const emailShipment = async (
  trackingNumber: string,
  emailAddress: string,
  sendQrCode = false,
): Promise<{ successful: boolean }> => {
  try {
    await axiosServiceV2.post(
      `/history/${trackingNumber}/email/${emailAddress}?sendQrCode=${sendQrCode}`,
    );
    return { successful: true };
  } catch (err) {
    return { successful: false };
  }
};

export const getShipmentHistory = async (
  queryParams: ShipmentsQueryParams = defaultShipmentsQueryParams,
): Promise<HistoryItemType[] | null> => {
  try {
    const queryString = getQueryStringForShipments(
      '/history/shipments?orderBy=Ship_DateTime&OrderByDescending=true',
      queryParams,
    );

    const res: AxiosResponse = await axiosServiceV2.get(queryString);
    return res.data.map((item) => filterShipmentHistoryFromResponse(item));
  } catch {
    return null;
  }
};

export const getShipmentsSummary = async (
  queryParams: ShipmentsQueryParams = defaultShipmentsQueryParams,
): Promise<ShipmentSummaryItem[] | null> => {
  try {
    const queryString = getQueryStringForShipments(
      '/history/shipments/summary?',
      queryParams,
    );

    const res: AxiosResponse = await axiosService.get(queryString);
    return res.data;
  } catch {
    return null;
  }
};

export const getShipment = async (trackingNumber: string): Promise<HistoryItemType | null> => {
  try {
    const res: AxiosResponse = await axiosServiceV2.get(`/history/shipments/${trackingNumber}`);
    return filterShipmentHistoryFromResponse(res.data);
  } catch {
    return null;
  }
};

export const getTableColumns = async (): Promise<string[] | null> => {
  try {
    const res: AxiosResponse = await axiosService.get('/history/column-order');
    return res.data.columnOrder;
  } catch {
    return null;
  }
};

export const setTableColumns = async (columnOrder: string[]): Promise<boolean> => {
  try {
    await axiosService.put('/history/column-order', { columnOrder });
    return true;
  } catch {
    return false;
  }
};

export const duplicateShipment = async (
  trackingNumber: string,
  force = 0,
): Promise<HistoryTrackingResponse & GlobalResponseType> => {
  try {
    const res: AxiosResponse = await axiosService.post(`/history/${trackingNumber}/duplicate?force=${force}`);
    return {
      ...res.data,
      successful: true,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const getTrackingDetails = async (
  trackingNumber: string,
  carrierTrackingNumber: string,
): Promise<TrackingDetailsType | null> => {
  try {
    const res: AxiosResponse = await axiosService.get(`/history/tracking/${trackingNumber}/${carrierTrackingNumber}`);
    return res.data;
  } catch {
    return null;
  }
};

export const getAddressMap = async (
  payload: AddressMapPayload,
): Promise<AddressMapResponse> => {
  try {
    const res: AxiosResponse = await axiosService.post('/maps', payload);

    return res.data;
  } catch {
    return { base64StringMap: '' };
  }
};

export const getCommercialInvoiceGeneralDescriptions = async (
  search: string,
): Promise<string[] | null> => {
  try {
    const res: AxiosResponse = await axiosService.get(
      `/history/commercial-invoice/general-description-of-Goods?search=${search}&Limit=10`,
    );
    return res.data.descriptions;
  } catch {
    return null;
  }
};

export const exportShipmentHistory = async ({
  myAccount,
  isDontShowSharedUsers,
  deleted,
  dateRange,
}: ExportShipmentHistoryDto): Promise<string | null> => {
  try {
    const res: AxiosResponse = await axiosService.get<string>(
      `/data-export/shipment-history/${dateRange.fromDate}/${dateRange.toDate}?myAccount=${myAccount}&isDontShowSharedUsers=${isDontShowSharedUsers}&deleted=${deleted}`,
    );
    return res.data;
  } catch {
    return null;
  }
};
